"use client"

/**
 * The hero's change of still (Eugene, 13.10): a ring of slow, wide ripples
 * spreads from beyond the left edge, the new still inside it and a faint
 * refracting wave on it.
 *
 * Both stills are drawn into a canvas laid over the hero with the stills' own
 * crop, the shader runs from 0 to 1, then the plain images take over: the
 * last frame is the new still, so the swap is invisible. Reduced motion, or a
 * browser without WebGL, gets a short plain fade instead.
 *
 * Picked and tuned from five candidates with a floating panel; that tryout is
 * parked on the local branch `eugene/hero-fx-tryout` (b39ae8211).
 */

import { useEffect, useEffectEvent, useRef } from "react"

/** Eugene's tuning, 13.10. */
const DURATION_MS = 2800
/** CSS cubic-bezier control points for the ring's progress. */
const EASE = [0.14, -0.27, 0.14, -0.13] as const

const FRAG = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uFrom;
uniform sampler2D uTo;
uniform vec2 uRes;
uniform vec2 uFromSize;
uniform vec2 uToSize;
uniform vec2 uPos;
uniform float uP;
const float PI = 3.14159265;
// Origin as a fraction of the frame, then the ring's soft edge, ripple
// density, ripple band (higher is narrower), refraction and shine.
const vec2 ORIGIN = vec2(-0.15, 0.62);
const float RING = 1.09;
const float FREQ = 5.0;
const float BAND = 1.3;
const float AMP = 0.002;
const float SHINE = 0.01;
// object-fit: cover at object-position uPos, as the still under the canvas.
vec2 cover(vec2 uv, vec2 img) {
  float a = uRes.x / uRes.y;
  float i = img.x / img.y;
  vec2 s = i > a ? vec2(a / i, 1.0) : vec2(1.0, i / a);
  return uPos * (1.0 - s) + clamp(uv, 0.0, 1.0) * s;
}
float far(vec2 k, float asp) { return length((k - ORIGIN) * vec2(asp, 1.0)); }
void main() {
  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
  float p = uP;
  float asp = uRes.x / uRes.y;
  vec2 q = (uv - ORIGIN) * vec2(asp, 1.0);
  float d = length(q);
  float maxd = max(max(far(vec2(0.0, 0.0), asp), far(vec2(1.0, 0.0), asp)), max(far(vec2(0.0, 1.0), asp), far(vec2(1.0, 1.0), asp)));
  float r = p * (maxd + RING);
  float m = 1.0 - smoothstep(r - RING, r, d);
  float k = d - r;
  float wave = sin(k * FREQ) * exp(-pow(k * BAND, 2.0)) * sin(PI * p);
  vec2 disp = normalize(q + 0.0001) / vec2(asp, 1.0) * wave * AMP;
  vec3 a = texture2D(uFrom, cover(uv + disp, uFromSize)).rgb;
  vec3 b = texture2D(uTo, cover(uv + disp * 1.3, uToSize)).rgb;
  gl_FragColor = vec4(mix(a, b, m) + wave * SHINE, 1.0);
}`

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() { vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`

/** The incoming still stays hidden while the canvas plays; reduced motion
 *  fades it in, and that animation's end closes the transition. */
export const HERO_FX_CSS = `
.hero-reveal[data-state] { opacity: 0; }
@keyframes hero-fade { from { opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .hero-reveal[data-state="run"] { opacity: 1; animation: hero-fade 0.4s both; }
}
`

/** CSS's cubic-bezier(), for the shader's progress. */
function cubicBezier([x1, y1, x2, y2]: readonly number[]) {
  const cx = 3 * x1
  const bx = 3 * (x2 - x1) - cx
  const ax = 1 - cx - bx
  const cy = 3 * y1
  const by = 3 * (y2 - y1) - cy
  const ay = 1 - cy - by
  const sx = (t: number) => ((ax * t + bx) * t + cx) * t
  const sy = (t: number) => ((ay * t + by) * t + cy) * t
  return (x: number) => {
    let lo = 0
    let hi = 1
    let t = x
    for (let i = 0; i < 24; i++) {
      if (sx(t) < x) lo = t
      else hi = t
      t = (lo + hi) / 2
    }
    return sy(t)
  }
}
const ease = cubicBezier(EASE)

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/** Plays the ripple from `from` to `to`, then calls `onDone`. Same box and
 *  crop as the hero's still (`.hero-shift`). Until both stills are in, the
 *  canvas is clear and the old still shows through. */
export function HeroRipple({
  from,
  to,
  onDone,
}: {
  from: string
  to: string
  onDone: () => void
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const done = useEffectEvent(onDone)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const gl2 = canvas.getContext("webgl2", { premultipliedAlpha: false })
    const gl = (gl2 ??
      canvas.getContext("webgl", {
        premultipliedAlpha: false,
      })) as WebGLRenderingContext | null
    if (!gl) {
      done()
      return
    }
    let raf = 0
    let cancelled = false
    const cleanup: (() => void)[] = []

    const compile = (type: number, src: string) => {
      const s = gl.createShader(type)!
      gl.shaderSource(s, src)
      gl.compileShader(s)
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
        throw new Error(gl.getShaderInfoLog(s) ?? "shader")
      return s
    }

    Promise.all([loadImage(from), loadImage(to)])
      .then(([a, b]) => {
        if (cancelled) return
        const prog = gl.createProgram()!
        gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT))
        gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG))
        gl.linkProgram(prog)
        gl.useProgram(prog)
        cleanup.push(() => gl.deleteProgram(prog))

        const buf = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, buf)
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([-1, -1, 3, -1, -1, 3]),
          gl.STATIC_DRAW
        )
        const loc = gl.getAttribLocation(prog, "aPos")
        gl.enableVertexAttribArray(loc)
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
        cleanup.push(() => gl.deleteBuffer(buf))

        // Mipmaps (WebGL 2 only, the stills are not powers of two) keep a 4K
        // still from shimmering when drawn smaller.
        const texture = (img: HTMLImageElement, unit: number) => {
          const t = gl.createTexture()
          gl.activeTexture(gl.TEXTURE0 + unit)
          gl.bindTexture(gl.TEXTURE_2D, t)
          gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            img
          )
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
          if (gl2) {
            gl.generateMipmap(gl.TEXTURE_2D)
            gl.texParameteri(
              gl.TEXTURE_2D,
              gl.TEXTURE_MIN_FILTER,
              gl.LINEAR_MIPMAP_LINEAR
            )
          } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
          }
          cleanup.push(() => gl.deleteTexture(t))
        }
        texture(a, 0)
        texture(b, 1)
        const u = (n: string) => gl.getUniformLocation(prog, n)
        gl.uniform1i(u("uFrom"), 0)
        gl.uniform1i(u("uTo"), 1)
        gl.uniform2f(u("uFromSize"), a.naturalWidth, a.naturalHeight)
        gl.uniform2f(u("uToSize"), b.naturalWidth, b.naturalHeight)
        // The hero's object-position: 100% 0% from the tablet up, 70% 0% on
        // a phone (HERO_REVEAL_CSS).
        gl.uniform2f(u("uPos"), window.innerWidth < 768 ? 0.7 : 1, 0)

        const start = performance.now()
        const frame = (now: number) => {
          const dpr = Math.min(window.devicePixelRatio || 1, 2)
          const w = Math.round(canvas.clientWidth * dpr)
          const h = Math.round(canvas.clientHeight * dpr)
          if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w
            canvas.height = h
          }
          gl.viewport(0, 0, w, h)
          const raw = Math.min((now - start) / DURATION_MS, 1)
          gl.uniform2f(u("uRes"), w, h)
          gl.uniform1f(u("uP"), ease(raw))
          gl.drawArrays(gl.TRIANGLES, 0, 3)
          if (raw < 1) raf = requestAnimationFrame(frame)
          else done()
        }
        raf = requestAnimationFrame(frame)
      })
      .catch((e) => {
        console.error("hero transition", e)
        if (!cancelled) done()
      })

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      cleanup.forEach((f) => f())
    }
  }, [from, to])

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="hero-shift pointer-events-none absolute top-0 left-0 h-full w-full"
    />
  )
}
